import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import useTema from '../../hooks/useTema';
import Modal from '../../components/ui/Modal';
import { useListas, nomeCliente, primeiroNome } from './comum';

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const GRUPOS = ['Todo dia', 'Toda semana', 'Início do mês', 'Manutenção', 'Fim do mês', 'Contas da Legacy'];
const ORDEM_GRUPOS = (g) => { const i = GRUPOS.indexOf(g); return i < 0 ? 99 : i; };

const rotuloDiaMes = (n) => (n === -1 ? 'último dia' : n < 0 ? `${-n}º dia antes do fim` : `dia ${n}`);
export const descreverRegra = (r) => {
  if (r.frequencia === 'diaria') {
    const d = [...(r.dias_semana || [])].sort();
    if (d.join() === '1,2,3,4,5') return 'Todo dia útil (seg a sex)';
    if (d.length === 7) return 'Todos os dias';
    if (d.join() === '1,2,3,4,5,6') return 'De segunda a sábado';
    return `Todo ${d.map((x) => DIAS[x]).join(', ')}`;
  }
  if (r.frequencia === 'semanal') {
    return r.dia_inicio === r.dia_prazo
      ? `Toda semana, na ${DIAS[r.dia_inicio]}`
      : `Toda semana, de ${DIAS[r.dia_inicio]} a ${DIAS[r.dia_prazo]}`;
  }
  if (r.dia_inicio === -5 && r.dia_prazo === -1) return 'Todo mês, nos últimos 5 dias';
  if (r.dia_inicio === 1 && r.dia_prazo === -1) return 'Todo mês, ao longo do mês';
  return `Todo mês, do ${rotuloDiaMes(r.dia_inicio)} ao ${rotuloDiaMes(r.dia_prazo)}`;
};

const OPCOES_DIA_MES = [
  ...Array.from({ length: 28 }, (_, i) => [i + 1, `Dia ${i + 1}`]),
  [-7, '7 dias antes do fim'], [-5, '5 dias antes do fim'], [-3, '3 dias antes do fim'], [-1, 'Último dia do mês'],
];

function FormRotina({ inicial, onFechar, onSalva }) {
  const { pessoas, clientes } = useListas();
  const [f, setF] = useState(inicial);
  const [salvando, setSalvando] = useState(false);
  const s = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleDia = (d) => s('dias_semana', f.dias_semana.includes(d) ? f.dias_semana.filter((x) => x !== d) : [...f.dias_semana, d]);

  // O grupo acompanha a frequência, para a rotina não ficar arquivada no lugar errado
  const grupoPara = (fr, atual) => {
    if (fr === 'diaria') return 'Todo dia';
    if (fr === 'semanal') return ['Todo dia', 'Início do mês', 'Manutenção', 'Fim do mês'].includes(atual) ? 'Toda semana' : atual;
    return ['Todo dia', 'Toda semana'].includes(atual) ? 'Início do mês' : atual;
  };
  const mudarFrequencia = (fr) => setF((p) => ({
    ...p, frequencia: fr, grupo: grupoPara(fr, p.grupo),
    dia_inicio: fr === 'semanal' ? 1 : fr === 'mensal' ? 1 : p.dia_inicio,
    dia_prazo: fr === 'semanal' ? 5 : fr === 'mensal' ? 5 : p.dia_prazo,
  }));

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    const corpo = { ...f, dia_inicio: Number(f.dia_inicio), dia_prazo: Number(f.dia_prazo) };
    try {
      if (f.id) await api.put(`/pauta/rotinas/${f.id}`, corpo);
      else await api.post('/pauta/rotinas', corpo);
      toast.success('Rotina salva. Já vale para a pauta.');
      onSalva();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Não foi possível salvar.');
    } finally { setSalvando(false); }
  };

  return (
    <Modal open onClose={onFechar} title={f.id ? 'Editar rotina' : 'Nova rotina'} size="md">
      <form onSubmit={salvar} className="space-y-3">
        <div>
          <label className="form-label">Atividade *</label>
          <input className="form-input" value={f.titulo} onChange={(e) => s('titulo', e.target.value)} required autoFocus
            placeholder="Ex.: Relatório do mês anterior por cliente" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Responsável</label>
            <select className="form-input" value={f.responsavel_id} onChange={(e) => s('responsavel_id', e.target.value)} required>
              <option value="">Escolha…</option>
              {pessoas.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Grupo</label>
            <select className="form-input" value={f.grupo || ''} onChange={(e) => s('grupo', e.target.value)}>
              {GRUPOS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="form-label">Frequência</label>
          <div className="flex gap-2">
            {[['diaria', 'Diária'], ['semanal', 'Semanal'], ['mensal', 'Mensal']].map(([v, l]) => (
              <button type="button" key={v} onClick={() => mudarFrequencia(v)}
                className={f.frequencia === v ? 'btn-primary' : 'btn-secondary'} style={{ flex: 1 }}>{l}</button>
            ))}
          </div>
        </div>

        {f.frequencia === 'diaria' && (
          <div>
            <label className="form-label">Em quais dias</label>
            <div className="flex gap-1.5 flex-wrap">
              {DIAS.map((d, i) => (
                <button type="button" key={d} onClick={() => toggleDia(i)}
                  className={f.dias_semana.includes(i) ? 'btn-primary' : 'btn-secondary'} style={{ padding: '6px 10px' }}>{d}</button>
              ))}
            </div>
          </div>
        )}
        {f.frequencia === 'semanal' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Entra na pauta na</label>
              <select className="form-input" value={f.dia_inicio} onChange={(e) => s('dia_inicio', e.target.value)}>
                {DIAS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Prazo na</label>
              <select className="form-input" value={f.dia_prazo} onChange={(e) => s('dia_prazo', e.target.value)}>
                {DIAS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </div>
          </div>
        )}
        {f.frequencia === 'mensal' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Entra na pauta no</label>
              <select className="form-input" value={f.dia_inicio} onChange={(e) => s('dia_inicio', e.target.value)}>
                {OPCOES_DIA_MES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Prazo no</label>
              <select className="form-input" value={f.dia_prazo} onChange={(e) => s('dia_prazo', e.target.value)}>
                {OPCOES_DIA_MES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
        )}

        <div>
          <label className="form-label">Cliente</label>
          <select className="form-input" value={f.cliente_id || ''} onChange={(e) => s('cliente_id', e.target.value)}>
            <option value="">Todos / interno</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{nomeCliente(c)}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Detalhes</label>
          <textarea className="form-input" rows={2} value={f.descricao || ''} onChange={(e) => s('descricao', e.target.value)}
            placeholder="Como fazer, links, observações (opcional)" />
        </div>
        <div className="flex gap-2 pt-1">
          <button className="btn-primary flex-1" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar rotina'}</button>
          <button type="button" className="btn-secondary" onClick={onFechar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

export default function Rotinas() {
  const t = useTema();
  const [rotinas, setRotinas] = useState([]);
  const [pessoaFiltro, setPessoaFiltro] = useState('');
  const [editando, setEditando] = useState(null);

  const carregar = useCallback(() => {
    api.get('/pauta/rotinas').then((r) => setRotinas(r.data || [])).catch(() => toast.error('Erro ao carregar rotinas.'));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const pessoas = useMemo(() => {
    const m = new Map();
    rotinas.forEach((r) => m.set(r.responsavel_id, r.responsavel_nome));
    return [...m.entries()];
  }, [rotinas]);

  const visiveis = rotinas.filter((r) => !pessoaFiltro || r.responsavel_id === pessoaFiltro);
  const grupos = visiveis.reduce((acc, r) => {
    const chave = `${r.responsavel_nome}|${r.grupo || 'Outras'}`;
    (acc[chave] = acc[chave] || []).push(r);
    return acc;
  }, {});
  const chaves = Object.keys(grupos).sort((a, b) => {
    const [pa, ga] = a.split('|'); const [pb, gb] = b.split('|');
    return pa.localeCompare(pb) || ORDEM_GRUPOS(ga) - ORDEM_GRUPOS(gb);
  });

  const alternarAtiva = async (r) => {
    try {
      await api.put(`/pauta/rotinas/${r.id}`, { ...r, ativo: !r.ativo });
      toast.success(r.ativo ? 'Rotina pausada.' : 'Rotina ativada.');
      carregar();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro ao atualizar.'); }
  };

  const excluir = async (r) => {
    if (!window.confirm(`Remover a rotina "${r.titulo}"? Ela sai da lista e da pauta; o histórico do que já foi feito continua salvo.`)) return;
    try { await api.delete(`/pauta/rotinas/${r.id}`); toast.success('Rotina removida.'); carregar(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro ao remover.'); }
  };

  const nova = () => setEditando({
    titulo: '', descricao: '', responsavel_id: pessoaFiltro || '', cliente_id: '', grupo: 'Toda semana',
    frequencia: 'semanal', dias_semana: [1, 2, 3, 4, 5], dia_inicio: 1, dia_prazo: 5,
  });

  let pessoaAnterior = null;

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: t.textTitle }}>Rotinas</h1>
          <p style={{ fontSize: 13, color: t.textMuted }}>O checklist fixo de cada pessoa. Entra sozinho na pauta, no dia certo e com prazo.</p>
        </div>
        <button className="btn-primary" onClick={nova}>+ Nova rotina</button>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {[['', 'Todos'], ...pessoas].map(([id, nome]) => (
          <button key={id || 'todos'} onClick={() => setPessoaFiltro(id)}
            style={{
              padding: '6px 14px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
              border: `1px solid ${pessoaFiltro === id ? '#6C4CF5' : t.border}`,
              background: pessoaFiltro === id ? 'rgba(108,76,245,.15)' : 'transparent',
              color: pessoaFiltro === id ? '#A78BFA' : t.textSecond,
            }}>
            {id ? primeiroNome(nome) : nome}
          </button>
        ))}
      </div>

      {chaves.map((chave) => {
        const [pessoa, grupo] = chave.split('|');
        const mostraPessoa = pessoa !== pessoaAnterior;
        pessoaAnterior = pessoa;
        return (
          <div key={chave}>
            {mostraPessoa && <h2 style={{ fontSize: 16, fontWeight: 800, color: t.text, margin: '22px 0 4px' }}>{pessoa}</h2>}
            <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '.06em', margin: '12px 0 6px' }}>{grupo}</p>
            <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden' }}>
              {grupos[chave].map((r, i) => (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', flexWrap: 'wrap',
                  borderTop: i ? `1px solid ${t.border}` : 'none', opacity: r.ativo ? 1 : 0.5,
                }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <p style={{ fontSize: 14, color: t.text }}>{r.titulo}</p>
                    <p style={{ fontSize: 12, color: t.textMuted }}>
                      {descreverRegra(r)}{r.cliente_nome ? ` · ${r.cliente_nome}` : ''}{r.ativo ? '' : ' · pausada'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 14 }}>
                    {[['Editar', () => setEditando({ ...r, dias_semana: r.dias_semana || [1, 2, 3, 4, 5] })],
                      [r.ativo ? 'Pausar' : 'Ativar', () => alternarAtiva(r)],
                      ['Remover', () => excluir(r)]].map(([l, a]) => (
                      <button key={l} onClick={a} style={{ fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: l === 'Remover' ? '#EF4444' : t.textSecond }}>{l}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {rotinas.length === 0 && <p style={{ color: t.textMuted, fontSize: 14 }}>Nenhuma rotina cadastrada ainda.</p>}

      {editando && <FormRotina inicial={editando} onFechar={() => setEditando(null)} onSalva={() => { setEditando(null); carregar(); }} />}
    </div>
  );
}
